import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

const buttonVariants = cva(
  cn(
    /* sorted */
    "inline-flex",
    "items-center justify-center gap-2",
    "text-sm font-medium whitespace-nowrap",
    "rounded-full cursor-pointer outline-none",
    "transition-[color,box-shadow]",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5 [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        primary: "",
        neutral: "",
        negative: "",
      },
      kind: {
        solid: "",
        soft: "",
        outline: "bg-transparent border",
        ghost: "",
        link: "underline",
      },
      size: {
        xs: "py-0.5 px-3 gap-1 h-6 text-sm font-normal",
        sm: "py-1 px-3 gap-2 h-7 font-normal",
        md: "py-1.5 px-4 gap-2 h-8",
        lg: "py-2 px-4 gap-2 h-9",
        xl: "py-2.5 px-5 gap-2 h-10",
        "icon-xs": "h-6 w-6",
        "icon-sm": "h-7 w-7",
        "icon-md": "h-8 w-8",
        "icon-lg": "h-9 w-9",
        "icon-xl": "h-10 w-10",
      },
    },
    compoundVariants: [
      // SOLID
      {
        kind: "solid",
        variant: "primary",
        class: "bg-purple-600 text-white hover:bg-purple-700",
      },
      {
        kind: "solid",
        variant: "neutral",
        class:
          "bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-200",
      },
      {
        kind: "solid",
        variant: "negative",
        class: "bg-red-600 text-white hover:bg-red-700",
      },
      // SOFT
      {
        kind: "soft",
        variant: "primary",
        class:
          "bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300",
      },
      {
        kind: "soft",
        variant: "neutral",
        class:
          "bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300",
      },
      {
        kind: "soft",
        variant: "negative",
        class:
          "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300",
      },
      // OUTLINE
      {
        kind: "outline",
        variant: "primary",
        class:
          "border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300",
      },
      {
        kind: "outline",
        variant: "neutral",
        class:
          "border-neutral-300 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300",
      },
      {
        kind: "outline",
        variant: "negative",
        class:
          "border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300",
      },
      // GHOST
      {
        kind: "ghost",
        variant: "primary",
        class:
          "text-purple-700 hover:bg-purple-100 dark:text-purple-300 dark:hover:bg-purple-900/30",
      },
      {
        kind: "ghost",
        variant: "neutral",
        class:
          "text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800",
      },
      {
        kind: "ghost",
        variant: "negative",
        class:
          "text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/30",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      kind: "solid",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Icon displayed before the button text */
    prefixIcon?: React.ReactNode;
    /** Icon displayed after the button text */
    suffixIcon?: React.ReactNode;
  };

function Button({
  className,
  variant,
  kind,
  size,
  type = "button",
  asChild = false,
  prefixIcon,
  suffixIcon,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  // Type assertion to work around csstype version mismatch between @radix-ui/react-slot and @types/react
  const Component = Comp as React.ElementType;

  return (
    <Component
      data-slot="button"
      className={cn(buttonVariants({ kind, size, variant }), className)}
      type={type}
      {...props}
    >
      {prefixIcon}
      {children}
      {suffixIcon}
    </Component>
  );
}

export { Button, type ButtonProps, buttonVariants };
